/**
 * Modal — Snel artikel aanmaken vanuit Goederen ontvangen.
 * Wanneer een gescande EAN/code niet bestaat, kan de gebruiker hier een minimaal artikel
 * direct aanmaken zonder de inventarispagina te verlaten.
 */
import { useState, useEffect, useRef } from 'react';
import { Plus, X, Package } from '@phosphor-icons/react';

export default function SnelArtikelModal({ open, initial, onClose, onSave }) {
  const [form, setForm] = useState({
    art_code: '', ean: '', omschrijving: '', categorie: '',
    aankoopprijs: '', verkoopprijs: '', eenheid: 'st',
  });
  const [busy, setBusy] = useState(false);
  const codeRef = useRef(null);

  useEffect(() => {
    if (open && initial) {
      setForm({
        art_code: initial.code || '',
        ean: initial.ean || '',
        omschrijving: '',
        categorie: '',
        aankoopprijs: '',
        verkoopprijs: '',
        eenheid: 'st',
      });
      setTimeout(() => codeRef.current?.focus(), 100);
    }
  }, [open, initial]);

  if (!open) return null;

  const submit = async () => {
    if (!form.art_code.trim()) return;
    if (!form.omschrijving.trim()) return;
    setBusy(true);
    try {
      await onSave(form);
    } finally {
      setBusy(false);
    }
  };

  const fld = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="snel-artikel-modal"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-lg overflow-hidden border-2 border-purple-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-purple-50 border-b-2 border-purple-200 px-5 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Package size={28} weight="duotone" className="text-purple-700" />
            <div>
              <div className="text-lg font-bold text-purple-900">Onbekende code — nieuw artikel?</div>
              <div className="text-xs text-purple-700">
                {initial?.ean
                  ? <>EAN <span className="font-mono bg-purple-100 px-1 rounded">{initial.ean}</span> niet gevonden</>
                  : <>Code <span className="font-mono bg-purple-100 px-1 rounded">{initial?.code}</span> niet gevonden</>}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/40 text-purple-900">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Artikelcode *</label>
              <input
                ref={codeRef}
                type="text"
                value={form.art_code}
                onChange={e => fld('art_code', e.target.value)}
                className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded focus:border-purple-600 focus:outline-none"
                data-testid="snel-art-code"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">EAN / Barcode</label>
              <input
                type="text"
                value={form.ean}
                onChange={e => fld('ean', e.target.value)}
                className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded focus:border-purple-600 focus:outline-none"
                data-testid="snel-art-ean"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Omschrijving *</label>
            <input
              type="text"
              value={form.omschrijving}
              onChange={e => fld('omschrijving', e.target.value)}
              placeholder="bv. Schroef hout 4x40 mm"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:border-purple-600 focus:outline-none"
              data-testid="snel-art-omschr"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Aankoopprijs (€)</label>
              <input
                type="number"
                step="0.01"
                value={form.aankoopprijs}
                onChange={e => fld('aankoopprijs', e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 text-sm text-right border border-gray-300 rounded focus:border-purple-600 focus:outline-none"
                data-testid="snel-art-ap"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Verkoopprijs (€)</label>
              <input
                type="number"
                step="0.01"
                value={form.verkoopprijs}
                onChange={e => fld('verkoopprijs', e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 text-sm text-right border border-gray-300 rounded focus:border-purple-600 focus:outline-none"
                data-testid="snel-art-vp"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Eenheid</label>
              <select
                value={form.eenheid}
                onChange={e => fld('eenheid', e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:border-purple-600 focus:outline-none"
              >
                <option value="st">stuk</option>
                <option value="kg">kg</option>
                <option value="m">m</option>
                <option value="m2">m²</option>
                <option value="m3">m³</option>
                <option value="l">liter</option>
                <option value="doos">doos</option>
                <option value="pak">pak</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Categorie</label>
            <input
              type="text"
              value={form.categorie}
              onChange={e => fld('categorie', e.target.value)}
              placeholder="bv. Schroeven, Verf, …"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:border-purple-600 focus:outline-none"
            />
          </div>

          <div className="text-[11px] text-gray-500 pt-1">
            Het artikel wordt aangemaakt met voorraad 0 en {parseFloat(initial?.aantal) || 1} stuk(s) wordt direct
            toegevoegd aan de ontvangst-lijst. Je kan later alle details aanvullen via Artikels.
          </div>
        </div>

        <div className="bg-gray-50 border-t border-gray-200 px-5 py-3 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
          >
            Annuleren
          </button>
          <button
            onClick={submit}
            disabled={busy || !form.art_code.trim() || !form.omschrijving.trim()}
            className="px-4 py-2 text-sm font-semibold text-white bg-purple-700 hover:bg-purple-800 rounded inline-flex items-center gap-1.5 disabled:bg-gray-300 disabled:cursor-not-allowed"
            data-testid="snel-art-opslaan"
          >
            <Plus size={14} /> {busy ? 'Bezig…' : 'Aanmaken & toevoegen'}
          </button>
        </div>
      </div>
    </div>
  );
}
